import React from 'react';
import { Flame, Users, AlertCircle } from 'lucide-react';

/**
 * Visual cabin availability indicator to drive urgency.
 *
 * Levels:
 *  - 0           → "Complet"
 *  - 1           → "Dernière cabine !" (red, pulse)
 *  - 2           → "Plus que 2 cabines" (orange, attention)
 *  - 3           → "3 cabines disponibles" (amber, soft)
 *  - 4+ (= max)  → discrete green pill
 */
const CabinScarcity = ({
  available,
  total = 4,
  variant = 'default', // 'default' | 'compact' | 'large'
  showProgress = true,
  className = '',
}) => {
  if (available === undefined || available === null) return null;
  const left = Number(available);
  const max = Number(total) || 4;

  // Determine urgency level
  let level = 'plenty';
  if (left <= 0) level = 'full';
  else if (left === 1) level = 'critical';
  else if (left === 2) level = 'high';
  else if (left === 3) level = 'medium';

  const config = {
    full: {
      icon: AlertCircle,
      label: 'Complet',
      sub: 'Plus de cabines disponibles',
      bg: 'bg-slate-100',
      border: 'border-slate-200',
      text: 'text-slate-500',
      dot: 'bg-slate-400',
      pulse: false,
    },
    critical: {
      icon: Flame,
      label: 'Dernière cabine !',
      sub: 'À réserver très vite',
      bg: 'bg-gradient-to-r from-red-500 to-rose-600',
      border: 'border-red-600',
      text: 'text-white',
      dot: 'bg-white',
      pulse: true,
    },
    high: {
      icon: Flame,
      label: 'Plus que 2 cabines',
      sub: 'Forte demande',
      bg: 'bg-gradient-to-r from-orange-500 to-red-500',
      border: 'border-orange-500',
      text: 'text-white',
      dot: 'bg-white',
      pulse: true,
    },
    medium: {
      icon: Users,
      label: '3 cabines disponibles',
      sub: 'Bientôt complet',
      bg: 'bg-amber-50',
      border: 'border-amber-300',
      text: 'text-amber-900',
      dot: 'bg-amber-500',
      pulse: false,
    },
    plenty: {
      icon: Users,
      label: `${left} cabines disponibles`,
      sub: 'Réservez en toute tranquillité',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-800',
      dot: 'bg-emerald-500',
      pulse: false,
    },
  };

  const c = config[level];
  const Icon = c.icon;
  const filled = max - left;
  const pct = Math.min(100, Math.max(0, (filled / max) * 100));

  // Compact variant: 1 line pill (used in tables / dense rows)
  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-bold whitespace-nowrap ${c.bg} ${c.border} ${c.text} ${className}`}
        data-testid={`cabin-scarcity-${level}`}
      >
        {c.pulse && (
          <span className="relative flex h-1.5 w-1.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${c.dot} opacity-75`} />
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${c.dot}`} />
          </span>
        )}
        <Icon className="h-3 w-3" />
        {c.label}
      </span>
    );
  }

  // Large variant : full block (used on reservation page)
  if (variant === 'large') {
    return (
      <div
        className={`relative rounded-2xl border-2 p-4 ${c.bg} ${c.border} ${className}`}
        data-testid={`cabin-scarcity-${level}-large`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${level === 'plenty' || level === 'medium' || level === 'full' ? 'bg-white/60' : 'bg-white/20'}`}>
            <Icon className={`h-6 w-6 ${c.text}`} />
            {c.pulse && (
              <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${c.dot} opacity-75`} />
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${c.dot}`} />
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-display text-lg sm:text-xl font-bold ${c.text} leading-tight`}>
              {c.label}
            </div>
            <div className={`text-xs sm:text-sm ${c.text} opacity-80`}>{c.sub}</div>
            {showProgress && (
              <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${level === 'critical' || level === 'high' ? 'bg-white/25' : 'bg-white/60'}`}>
                <div
                  className={`h-full ${level === 'critical' || level === 'high' ? 'bg-white' : c.dot}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default variant (medium) : suitable for cruise detail rows
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs sm:text-sm font-semibold ${c.bg} ${c.border} ${c.text} ${className}`}
      data-testid={`cabin-scarcity-${level}`}
    >
      {c.pulse && (
        <span className="relative flex h-2 w-2">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${c.dot} opacity-75`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${c.dot}`} />
        </span>
      )}
      <Icon className="h-3.5 w-3.5" />
      <span className="leading-tight">{c.label}</span>
    </div>
  );
};

export default CabinScarcity;
